import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { Phone } from 'lucide-react';

import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import MainServicePage from './pages/MainServicePage';
import SubServicePage from './pages/SubServicePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ThankYouPage from './pages/ThankYouPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CardPage from './pages/CardPage';

import {
  initAnalytics,
  installCallTracker,
  captureUTMs,
  captureLandingContext,
  trackPageView,
} from './lib/analytics';

import './App.css';

const PHONE = "403-615-8770";

// Scroll to top + GA4 page_view on route change
const RouteEffects = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Ensure gtag is initialised before any page_view (child effects run before App's).
    initAnalytics();
    window.scrollTo(0, 0);
    trackPageView(pathname + (search || ''));
  }, [pathname, search]);

  return null;
};

// Layout wrapper component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white flex flex-col font-body">
      <Header />
      <div className="flex-grow">
        {children}
      </div>
      <Footer />

      {/* Sticky Mobile Call Button */}
      <a
        href={`tel:${PHONE}`}
        data-testid="mobile-call-btn"
        data-call-source="sticky_mobile_cta"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:hidden z-50 bg-gradient-to-r from-[#38BDF8] to-[#0284C7] text-white font-bold px-7 py-3.5 rounded-full shadow-2xl flex items-center gap-2 hover:shadow-cyan-300/60 transition-all"
        aria-label="Call DNB Plumbing"
      >
        <Phone className="w-5 h-5" />
        Call DNB Now
      </a>
    </div>
  );
};

function App() {
  useEffect(() => {
    initAnalytics();
    installCallTracker();
    captureUTMs();
    captureLandingContext();
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <RouteEffects />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              color: '#0f172a',
              border: '1px solid #BAE6FD',
            },
          }}
        />
      <Routes>
        {/* Standalone QR business-card landing — no header/footer */}
        <Route path="/card" element={<CardPage />} />

        <Route path="/" element={<Layout><HomePage /></Layout>} />

        {/* Static pages - must come before dynamic routes */}
        <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
        <Route path="/services/:serviceType" element={<Layout><ServiceDetailPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/thank-you" element={<Layout><ThankYouPage /></Layout>} />
        <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
        <Route path="/blog/:postId" element={<Layout><BlogPostPage /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />

        {/* Service Pages - must be last for dynamic routing */}
        <Route path="/:mainService/:subService" element={<Layout><SubServicePage /></Layout>} />
        <Route path="/:mainService" element={<Layout><MainServicePage /></Layout>} />
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
